/**
 * PURPOSE: Cabecera del detalle de una prueba de Atletismo.
 * RESPONSIBILITIES: Nombre, estado, DEMO, horario por fase (series / final con fecha, hora y cámara de
 *   llamadas), datos técnicos de `prueba.config` (implemento, altura de valla, tabla de batida,
 *   alturas, cronometraje, intentos), advertencias, los botones "Oficializar" y "Reabrir" y, junto a ellos, el grupo
 *   "Descargas" que le pasa el panel (`downloads`: planilla PDF y FinishLynx).
 * COLLABORATORS: PruebaDetailPanel.jsx.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/PruebaHeader.jsx
 */
import React from "react";
import { FiAlertTriangle, FiCheckCircle, FiClock, FiUnlock } from "react-icons/fi";

import { formatHeightCm } from "../../../utils/athleticsMarks.js";
import { TIMING_LABELS, WARNING_LABELS, formatShortDate, formatTime } from "../labels.js";
import { Badge, DemoBadge, PruebaStatusChip, Spinner, dangerButtonClass } from "./ui.jsx";

const ConfigChip = ({ label, value }) =>
  value ? (
    <span className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-600">
      <span className="font-semibold text-slate-400">{label}</span>
      <span className="font-bold text-slate-700">{value}</span>
    </span>
  ) : null;

const heightsText = (heights) => {
  if (!heights || typeof heights !== "object") return null;
  const parts = [];
  if (heights.startCm) parts.push(`desde ${formatHeightCm(heights.startCm)} m`);
  if (heights.stepCm) parts.push(`+${heights.stepCm} cm`);
  if (heights.untilCm) parts.push(`hasta ${formatHeightCm(heights.untilCm)} m`);
  return parts.length ? parts.join(" · ") : null;
};

const PruebaHeader = ({
  prueba,
  parentLabel,
  warnings,
  canOfficialize,
  canReopen,
  dirty,
  busy,
  onOfficialize,
  onReopen,
  downloads = null,
}) => {
  const config = prueba?.config || {};
  const official = prueba?.status === "official";
  const schedule = Array.isArray(prueba?.schedule) ? prueba.schedule : [];
  const list = Array.isArray(warnings) ? warnings : [];

  return (
    <div className="space-y-3 border-b border-slate-100 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-xl font-bold leading-7 text-slate-900">{prueba?.label || prueba?.name}</h3>
          {parentLabel ? (
            // Una sub-prueba se llama igual que la prueba suelta ("Salto largo Avanzados Varones"):
            // se dice siempre de qué combinada es para no capturar en la equivocada.
            <p className="text-sm font-semibold text-indigo-600">Prueba del {parentLabel}</p>
          ) : null}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <PruebaStatusChip status={prueba?.status} />
            {prueba?.isDemo ? <DemoBadge /> : null}
            {prueba?.code ? <Badge tone="indigo">{prueba.code}</Badge> : null}
            {Number(prueba?.pointsMultiplier) > 1 ? (
              <Badge tone="blue" title="Multiplicador de puntos">
                Puntos x{prueba.pointsMultiplier}
              </Badge>
            ) : null}
            {prueba?.scores === false ? (
              <Badge tone="slate" title="No suma puntos a la clasificación por universidad">
                No puntúa
              </Badge>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {downloads}
          {!official && canOfficialize ? (
            <button
              type="button"
              onClick={onOfficialize}
              disabled={Boolean(busy) || dirty}
              title={dirty ? "Guarda o descarta los cambios de la serie antes de oficializar." : undefined}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
            >
              {busy === "official" ? <Spinner /> : <FiCheckCircle aria-hidden="true" />}
              Oficializar
            </button>
          ) : null}
          {official && canReopen ? (
            <button
              type="button"
              onClick={onReopen}
              disabled={Boolean(busy)}
              className={dangerButtonClass}
            >
              {busy === "reopen" ? <Spinner /> : <FiUnlock aria-hidden="true" />}
              Reabrir
            </button>
          ) : null}
        </div>
      </div>

      {schedule.length ? (
        <div className="flex flex-wrap gap-2">
          {schedule.map((item, index) => (
            <div
              key={`${item.phase}-${index}`}
              className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"
            >
              <FiClock className="mt-0.5 text-indigo-500" aria-hidden="true" />
              <div className="text-xs leading-5">
                <p className="font-bold text-slate-800">
                  {item.label || item.phase}
                  {item.jornada ? <span className="ml-1 font-semibold text-slate-400">· J{item.jornada}</span> : null}
                </p>
                <p className="text-slate-600">
                  {formatShortDate(item.date)} {formatTime(item.time)}
                </p>
                {item.callOpen || item.callClose ? (
                  <p className="text-slate-500">
                    Cámara de llamadas: {formatTime(item.callOpen) || "—"}
                    {item.callClose ? ` – ${formatTime(item.callClose)}` : ""}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <ConfigChip label="Implemento" value={config.implement || prueba?.implement} />
        <ConfigChip label="Altura de valla" value={config.hurdleHeight} />
        <ConfigChip label="Tabla de batida" value={config.takeoffBoard} />
        <ConfigChip label="Alturas" value={heightsText(config.heights)} />
        <ConfigChip label="Cronometraje" value={TIMING_LABELS[config.timing] || config.timing} />
        <ConfigChip label="Intentos" value={config.attempts ? String(config.attempts) : null} />
      </div>

      {list.length ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="mb-1 flex items-center gap-1.5 text-xs font-black uppercase tracking-wide text-amber-800">
            <FiAlertTriangle aria-hidden="true" /> Advertencias ({list.length})
          </p>
          <ul className="list-disc space-y-0.5 pl-5 text-xs font-semibold text-amber-900">
            {list.map((warning, index) => (
              <li key={`${warning.code}-${index}`}>
                {WARNING_LABELS[warning.code] ? (
                  <span className="mr-1 font-black">{WARNING_LABELS[warning.code]}:</span>
                ) : null}
                {warning.message || warning.code}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default PruebaHeader;
